import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { FormContainer } from 'react-hook-form-mui'
import { notification } from '../../components/Notifications'
import { Box, Container } from '@mui/material'
import GenericAutocomplete from '../../components/FormElements/FormAutoComplete'
import SinglePageView from './SinglePageView'
import SectionBySectionView from './SectionBySectionView'
import PageHeader from '../../components/PageHeader'
import { renderAttributeChip } from './SurveyPreviewUtils'
import { AutoImportManager } from '../../APIManager'
import { useQuery } from '@tanstack/react-query'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import NotFoundCard from '../../components/NotFoundCard'

export enum PreviewTypes {
  SINGLE_PAGE_VIEW = 'Single-page View',
  STUDENT_EXPERIENCE = 'Student Experience',
}

export const previewTypesDropdown = [PreviewTypes.SINGLE_PAGE_VIEW, PreviewTypes.STUDENT_EXPERIENCE]

const SurveyPreviewFeature: React.FC = () => {
  const [previewType, setPreviewType] = useState<PreviewTypes>(PreviewTypes.SINGLE_PAGE_VIEW)

  const { slotId, sectionId } = useParams()

  const { data: survey, isLoading, isError } = useQuery({
    queryKey: ['dates', slotId, sectionId],
    queryFn: async () => await AutoImportManager.surveyPreview(slotId ?? '', sectionId ?? ''),
    enabled: !!slotId && !!sectionId,
    staleTime: 10000,
    retry: false
  })

  const handlePreviewTypeChange = (value: PreviewTypes | null): void => {
    setPreviewType(value as PreviewTypes)
  }

  const onSubmit = async (): Promise<void> => {
    notification('Survey Preview Completed', { variant: 'success' })
  }

  const allAttributes: Array<{ values: string[], op?: string }> = survey
    ? survey?.surveySections?.flatMap((section: any) => section?.attributes ?? [])
    : []

  const description = survey?.section?.abbreviation
    ? `${survey?.section?.abbreviation} | ${survey?.title}`
    : survey?.title

  if (isLoading) {
    return <LoadingPageV2 />
  }

  if (isError) {
    return <NotFoundCard/>
  }

  const previewTypeControl = <Box>
    <GenericAutocomplete
      defaultValue={previewTypesDropdown[0]}
      name="surveyPreviewType"
      getOptionLabel={(option: string) => option}
      getOptionValue={(option: string) => option}
      options={previewTypesDropdown}
      onChange={handlePreviewTypeChange}
      sx={{ width: '270px' }}
      wrapperStyle={{ minHeight: 'auto' }}
    />
  </Box>

  const showSurvey = (): JSX.Element => {
    switch (previewType) {
      case PreviewTypes.SINGLE_PAGE_VIEW:
        return <SinglePageView surveySections={survey?.surveySections} />
      case PreviewTypes.STUDENT_EXPERIENCE:
        return <SectionBySectionView surveySections={survey?.surveySections} onSubmit={onSubmit} />
      default:
        return <SectionBySectionView surveySections={survey?.surveySections} onSubmit={onSubmit} />
    }
  }

  return (
    <FormContainer>
      <PageHeader
        title={survey?.title || ''}
        attributes={
          <Box sx={{ mt: 1 }}>
            {allAttributes?.map((attr, index) => renderAttributeChip(attr, index))}
          </Box>
        }
        description={description}
        isCompactOnly
        controls={previewTypeControl}
      />

      <Box width="100%">
        <Container>
          {showSurvey()}
        </Container>
      </Box>
    </FormContainer>
  )
}

export default SurveyPreviewFeature
